import React, { useState } from 'react';
import { Edit3, Trash2, MapPin, DollarSign, Calendar, Users, Building2 } from 'lucide-react';
import EmptyState from '../../../components/ui/EmptyState';

const PropertyTable = ({ 
  properties, 
  communes, 
  selectedProperties, 
  onSelectionChange, 
  onEdit, 
  onDelete, 
  canManage 
}) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(properties?.map(p => p?.id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectProperty = (propertyId, checked) => {
    if (checked) {
      onSelectionChange?.(prev => [...prev, propertyId]);
    } else {
      onSelectionChange?.(prev => prev?.filter(id => id !== propertyId));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'reserved': return 'bg-yellow-100 text-yellow-800';
      case 'sold': return 'bg-blue-100 text-blue-800';
      case 'withdrawn': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'reserved': return 'Réservé';
      case 'sold': return 'Vendu';
      case 'withdrawn': return 'Retiré';
      default: return status;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'apartment': return 'Appartement';
      case 'house': return 'Maison';
      case 'land': return 'Terrain';
      case 'commercial': return 'Commercial';
      case 'villa': return 'Villa';
      default: return type;
    }
  };

  const getCommuneName = (communeId) => {
    const commune = communes?.find(c => c?.id === communeId);
    return commune ? `${commune?.name} (${commune?.district})` : 'N/A';
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 hover:text-blue-600 transition-colors"
    >
      <span>{children}</span>
      {sortField === field && (
        <span className={`text-xs ${sortOrder === 'asc' ? '▲' : '▼'}`} />
      )}
    </button>
  );

  if (!properties?.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <EmptyState
          iconName="Building2"
          title="Aucune propriété"
          description="Aucune propriété ne correspond à vos critères de recherche."
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {canManage && (
                <th scope="col" className="px-6 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={selectedProperties?.length === properties?.length}
                    onChange={(e) => handleSelectAll(e?.target?.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
              )}
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="title">Propriété</SortButton>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="commune_id">Commune</SortButton>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="property_type">Type</SortButton>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="status">Statut</SortButton>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="price">Prix</SortButton>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Détails
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agent
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortButton field="created_at">Créé le</SortButton>
              </th>
              {canManage && (
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {properties?.map((property) => (
              <tr key={property?.id} className="hover:bg-gray-50">
                {canManage && (
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedProperties?.includes(property?.id)}
                      onChange={(e) => handleSelectProperty(property?.id, e?.target?.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                )}
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{property?.title}</div>
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {property?.address}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {getCommuneName(property?.commune_id)}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getTypeLabel(property?.property_type)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(property?.status)}`}>
                    {getStatusLabel(property?.status)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                    {parseFloat(property?.price)?.toLocaleString()} FCFA
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="space-y-1">
                    {property?.bedrooms && (
                      <div>{property?.bedrooms} chambres</div>
                    )}
                    {property?.bathrooms && (
                      <div>{property?.bathrooms} sdb</div>
                    )}
                    {property?.surface_area && (
                      <div>{property?.surface_area} m²</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {property?.agent?.full_name ? (
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {property?.agent?.full_name}
                    </div>
                  ) : (
                    <span className="text-gray-400">Non assigné</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(property?.created_at)?.toLocaleDateString('fr-FR')}
                  </div>
                </td>
                {canManage && (
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onEdit?.(property)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Modifier"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete?.(property)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="bg-gray-50 px-6 py-4 border-t">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Total: {properties?.length} propriétés</span>
          {selectedProperties?.length > 0 && (
            <span>{selectedProperties?.length} propriétés sélectionnées</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyTable;